import styled from 'styled-components';
import { useTheme } from '../../../../theme/theme-context';
import { Bet, BetBonusType } from '../../api/betting-service/betting-service-types';
import { Caption, H2, LabelBold } from '@comeon/ui';
import { BetType } from '../../sportsbook-types';
import ShareBetModalTextKeys from './sportsbook-share-bet-modal-text-keys';
import normalizeReturns from '../../utils/normalize-returns';
import formatOdds from '../../utils/format-odds';
import React, { useMemo } from 'react';
import { Translation } from '../../../../translation/types';
import betEnumToTextKeyMap from '../../utils/sportsbook-bet-utils';
import FormattedAmount from '../../../../account/formatted-amount';
import { SettlementStatus } from '../../sportsbook-constants';
import {
	BetSettlementStatusToIndicatorStatusMap,
	returnsLabelByStatus
} from '../../components/bet/sportsbook-bet-helpers';
import SelectionInfo from '../../components/sportsbook-selection-info';
import { BulletIndicator } from '../../components/sportsbook-indicator';
import Icon, { ICON_ALL_SPORTS } from '../../../../icon';
import useOddsFormat from '../../hooks/use-odds-format';

// Helpers
const stakeBonusTypes = [BetBonusType.FREE_BET, BetBonusType.RISK_FREE_BET];

const Card = styled.div`
	position: relative;
	background: var(--sportsbook-my-bets-share-bet-card-bg);
	border-radius: 18px;
	padding: 20px 12px;
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: 12px;
	filter: drop-shadow(5px 10px 25px rgba(0, 0, 0, 0.25));
	overflow: hidden;
`;

const BackgroundIconWrapper = styled.div`
	z-index: -1; // To ensure icon is in background
	position: absolute; // to ensure gap on card is ignored
	bottom: 0;
	right: 0;
`;

const BackgroundIcon = styled(Icon)`
	position: absolute;
	bottom: -14px;
	right: -10px;
	height: 222px;
	width: 222px;
	fill: var(--sportsbook-my-bets-share-bet-card-icon-fill);
	opacity: 0.3;
`;

const VerticalDivider = styled.div`
	width: 100%;
	height: 1px;
	background-color: var(--sportsbook-border-primary);
`;

const HorizontalDivider = styled.div`
	height: 100%;
	width: 1px;
	background-color: var(--sportsbook-border-primary);
`;

const LogoWrapper = styled.div`
	width: 100px;
	svg {
		color: var(--sportsbook-my-bets-share-bet-card-logo-fill, inherit);
	}
`;

const BetTypeLabel = styled(H2)`
	text-align: center;
`;

const BetInfoList = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	text-align: center;
	width: 100%;
`;

const BetInfo = styled.div`
	flex: 1;
`;

const BetInfoTypeLabel = styled(Caption)`
	color: var(--sportsbook-text-supportive);
	margin-bottom: 5px;
`;

const BetInfoValueLabel = styled(LabelBold)``;

const BetItemList = styled.div`
	width: 100%;
	position: relative;
`;

const BetItem = styled.div`
	position: relative;
	display: grid;
	grid-template-columns: 1fr;
	width: 100%;

	&:not(:last-child) {
		margin-bottom: 12px;
	}
`;

const Bullet = styled(BulletIndicator)`
	position: absolute;
	left: 10px;
	top: 4.5px;
`;

const BetItemInfoWrapper = styled.div`
	padding: 0 12px;
	padding-left: calc(12px + 14px); // 12px of base padding + 14px of padding for status indicator
`;

const ComboConnectionBorder = styled.div<{ left: string }>`
	position: absolute;
	left: ${({ left }) => left};
	top: 15px;
	bottom: 13px;
	border-right: 1px dashed var(--sportsbook-connection-border-color, rgba(var(--base-06), 1));
`;

type ShareBetModalCardProps = {
	translations: Translation;
	bet: Bet;
	cardRef: React.RefObject<HTMLDivElement | null>;
};

const ShareBetModalCard: React.FC<ShareBetModalCardProps> = ({ bet, translations, cardRef }) => {
	const { Logo } = useTheme() as { Logo: React.FunctionComponent };
	const { oddsFormat } = useOddsFormat();

	const betTypeLabel: string = useMemo(() => {
		if (bet.type === BetType.COMBO) {
			return `${translations[ShareBetModalTextKeys.TICKET_TITLE_COMBO_LABEL]} ${
				translations[ShareBetModalTextKeys.SHARE_MODAL_BET]
			} @${formatOdds(bet.finalOdds, oddsFormat)}`;
		}
		if (bet.type === BetType.SYSTEM) {
			return `${translations[ShareBetModalTextKeys.TICKET_TITLE_SYSTEM_LABEL]} ${
				translations[betEnumToTextKeyMap[bet.systemBetType as string]]
			}`;
		}
		return translations[ShareBetModalTextKeys.TICKET_TITLE_SINGLE_LABEL];
	}, [bet.type, bet.systemBetType, bet.finalOdds, oddsFormat, translations]);

	const betStakeLabel = useMemo(() => {
		const hasStakeBonus = bet.bonus && stakeBonusTypes.some(stakeBonus => stakeBonus === bet.bonus?.type);
		if (hasStakeBonus && bet.bonus?.type === BetBonusType.FREE_BET) {
			return translations[ShareBetModalTextKeys.TICKET_FREE_BET_STAKE_LABEL];
		}
		if (hasStakeBonus && bet.bonus?.type === BetBonusType.RISK_FREE_BET) {
			return translations[ShareBetModalTextKeys.TICKET_RISK_FREE_BET_STAKE_LABEL];
		}
		return translations[ShareBetModalTextKeys.TICKET_STAKE_LABEL];
	}, [bet.bonus, translations]);

	const betReturnLabel = useMemo(() => {
		return bet.bonus?.type === BetBonusType.RISK_FREE_BET && bet.settlementStatus === SettlementStatus.LOST
			? translations[ShareBetModalTextKeys.TICKET_RETURNED_RISK_FREE_BET_LABEL]
			: translations[returnsLabelByStatus[bet.settlementStatus]];
	}, [bet.bonus?.type, bet.settlementStatus, translations]);

	return (
		<Card ref={cardRef}>
			<BackgroundIconWrapper>
				<BackgroundIcon type={ICON_ALL_SPORTS} />
			</BackgroundIconWrapper>
			<LogoWrapper>
				<Logo />
			</LogoWrapper>
			<VerticalDivider />
			<BetTypeLabel>{betTypeLabel}</BetTypeLabel>
			<BetInfoList>
				<BetInfo>
					<BetInfoTypeLabel mt={1}>{betStakeLabel}</BetInfoTypeLabel>
					<BetInfoValueLabel>
						<FormattedAmount amount={bet.stake} />
					</BetInfoValueLabel>
				</BetInfo>
				<HorizontalDivider />
				<BetInfo>
					<BetInfoTypeLabel mt={1}>{betReturnLabel}</BetInfoTypeLabel>
					<BetInfoValueLabel>
						{bet.finalReturns > 0 ? <FormattedAmount amount={normalizeReturns(bet.finalReturns)} /> : '-'}
					</BetInfoValueLabel>
				</BetInfo>
			</BetInfoList>
			<VerticalDivider />
			<BetItemList>
				{bet.type === BetType.COMBO && <ComboConnectionBorder left="13px" />}
				{bet.items.map(betItem => (
					<BetItem key={betItem.selectionId}>
						<Bullet indicatorStatus={BetSettlementStatusToIndicatorStatusMap[betItem.settlementStatus]} />
						<BetItemInfoWrapper>
							<SelectionInfo
								selectionName={betItem.selectionDisplayName}
								marketName={betItem.marketDisplayName}
								initOdds={betItem.initOdds}
								finalOdds={betItem.finalOdds}
								oddsFormat={oddsFormat}
								translations={translations}
								isBetBuilder={betItem.isBetBuilder}
								isPreCanned={betItem.isPreCanned}
								eventName={betItem.eventDisplayName}
							/>
						</BetItemInfoWrapper>
					</BetItem>
				))}
			</BetItemList>
		</Card>
	);
};

export default ShareBetModalCard;
